import { useEffect, useMemo, useRef } from 'react'
import { useField, useFormikContext } from 'formik'
import {
  useAdvertiserSettingsQuery,
  useSearchCopperOpportunitiesByCompanyQuery,
} from '../../../../__generated__/graphql'
import { useCurrentUser } from '../../../../utils/hooks/useUserContext'
import { useCompanyDetailsContext } from '../../../../utils/hooks/useCompanyDetailsContext'
import {
  ALL_COPPER_OPPORTUNITY_STATUSES,
  DEFAULT_COPPER_OPPORTUNITY_STAGE,
} from '../../../company/copper/copperOpportunitySearch.utils'
import { CampaignCreateAdgroupDraft } from './campaignCreateDrafts'
import { getOpportunityBudgetRemaining } from './campaignCopperBudget.utils'

export const useCampaignCreateCopperOpportunity = (): {
  showOpportunityPicker: boolean
  loading: boolean
  stageOptions: string[]
  opportunityOptions: Array<{ value: string; label: string }>
  opportunityBudgetRemaining: number
  selectedMonetaryValue: number | null
} => {
  const {
    me: {
      company: {
        userPermissions: { superAdmin },
      },
    },
  } = useCurrentUser()
  const {
    company: { id: companyId },
  } = useCompanyDetailsContext()
  const { values } = useFormikContext<{ adgroups?: CampaignCreateAdgroupDraft[] }>()
  const [, copperOpportunityIdMeta, copperOpportunityFieldHelpers] = useField('copperOpportunityId')
  const [, copperOpportunityStageMeta] = useField('copperOpportunityStage')

  const copperOpportunityStage =
    copperOpportunityStageMeta.value != null && copperOpportunityStageMeta.value !== ''
      ? String(copperOpportunityStageMeta.value)
      : undefined

  const { data: settingsData, loading: settingsLoading } = useAdvertiserSettingsQuery({
    variables: { id: companyId },
    skip: !superAdmin,
    fetchPolicy: 'no-cache',
  })

  const copperId = settingsData?.advertiser?.settings?.copperId?.trim() || null
  const showOpportunityPicker = Boolean(superAdmin && copperId)

  const { data: opportunitiesData, loading: opportunitiesLoading } = useSearchCopperOpportunitiesByCompanyQuery({
    skip: !showOpportunityPicker || !copperId,
    fetchPolicy: 'no-cache',
    variables: {
      params: {
        companyId: copperId ?? undefined,
        excludeLinkedCompanies: false,
        statuses: ALL_COPPER_OPPORTUNITY_STATUSES,
        ...(copperOpportunityStage ? { stages: [copperOpportunityStage] } : {}),
      },
    },
  })

  const opportunities = opportunitiesData?.searchCopperOpportunities ?? []

  const selectedCopperOpportunityId =
    copperOpportunityIdMeta.value != null && copperOpportunityIdMeta.value !== ''
      ? String(copperOpportunityIdMeta.value)
      : undefined

  const selectedMonetaryValue = useMemo(() => {
    if (!selectedCopperOpportunityId) return null
    const opportunity = opportunities.find((item) => item.id === selectedCopperOpportunityId)
    const value = opportunity?.monetaryValue
    if (value == null || !Number.isFinite(value)) return null
    return Math.round(value)
  }, [opportunities, selectedCopperOpportunityId])

  const stageOptions = useMemo(() => {
    const stages = new Set<string>([DEFAULT_COPPER_OPPORTUNITY_STAGE])
    if (copperOpportunityStage) stages.add(copperOpportunityStage)
    for (const opportunity of opportunities) if (opportunity.stage) stages.add(opportunity.stage)
    return Array.from(stages)
  }, [opportunities, copperOpportunityStage])

  const opportunityOptions = useMemo(
    () => opportunities.map((opportunity) => ({ value: opportunity.id, label: opportunity.name })),
    [opportunities]
  )

  const adgroups = values.adgroups ?? []
  const opportunityBudgetRemaining = getOpportunityBudgetRemaining(selectedMonetaryValue, adgroups)
  const previousStageRef = useRef(copperOpportunityStage)

  useEffect(() => {
    if (!showOpportunityPicker) copperOpportunityFieldHelpers.setValue(null)
  }, [showOpportunityPicker])

  useEffect(() => {
    if (previousStageRef.current === copperOpportunityStage) return
    previousStageRef.current = copperOpportunityStage
    copperOpportunityFieldHelpers.setValue(null)
  }, [copperOpportunityStage])

  return {
    showOpportunityPicker,
    loading: settingsLoading || (showOpportunityPicker && opportunitiesLoading),
    stageOptions,
    opportunityOptions,
    opportunityBudgetRemaining,
    selectedMonetaryValue,
  }
}
